import { useState, useCallback, useEffect, useMemo } from 'react';

import { getBasisTheoryApiKey } from '../../../react/config/payment';
import { ThreedsResource } from '../../core/resources/threeds';

import { useThreedsModal } from './useThreedsModal';
import { getGlobalApiClient } from './useApiQuery';

// Define supported 3DS providers
export type ThreedsProvider = 'basis_theory';

export interface PaymentInstrument {
    id: string;
    token: string | null;
    type: string;
    card: {
        maskedCardNumber?: string;
        expirationMonth?: number;
        expirationYear?: number;
    };
}

export interface ThreedsSession {
    id: string;
    sessionId: string;
    provider: string;
}

export interface ThreedsChallenge {
    sessionId: string;
    acsChallengeUrl: string;
    acsTransactionId: string;
    threeDSVersion: string;
}

export interface ThreedsOptions {
    provider?: ThreedsProvider;
}

export interface ThreedsHook {
    createSession: (paymentInstrument: PaymentInstrument, options?: ThreedsOptions) => Promise<ThreedsSession>;
    startChallenge: (challengeData: ThreedsChallenge, options?: ThreedsOptions) => Promise<any>;
    isLoading: boolean;
    error: Error | null;
}

interface UseThreedsOptions {
    defaultProvider?: ThreedsProvider;
}

export function useThreeds(options: UseThreedsOptions = {}): ThreedsHook {
    const { defaultProvider = 'basis_theory' } = options;
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<Error | null>(null);
    const { createThreedsModal, closeThreedsModal } = useThreedsModal();
    const [basisTheory3ds, setBasisTheory3ds] = useState<any>(null);
    const [_currentChallenge, _setCurrentChallenge] = useState<any>(null);

    // Create threeds resource client
    const threedsResource = useMemo(() => {
        try {
            return new ThreedsResource(getGlobalApiClient());
        } catch (error) {
            throw new Error('Failed to initialize threeds resource: ' + (error instanceof Error ? error.message : 'Unknown error'));
        }
    }, []);

    // Dynamically import BasisTheory3ds on the client side only
    useEffect(() => {
        let isMounted = true;

        const loadBasisTheory = async () => {
            try {
                // Dynamically import the library only on the client side
                const { BasisTheory3ds } = await import('@basis-theory/web-threeds');

                if (isMounted) {
                    setBasisTheory3ds(() => BasisTheory3ds);
                }
            } catch (err) {
                if (isMounted) {
                    setError(err instanceof Error ? err : new Error('Failed to load 3DS library'));
                }
            }
        };

        void loadBasisTheory();

        return () => {
            isMounted = false;
        };
    }, []);

    // Create a 3DS session with BasisTheory
    const createBasisTheorySession = useCallback(
        async (paymentInstrument: PaymentInstrument) => {
            if (!basisTheory3ds) {
                throw new Error('BasisTheory3ds not loaded yet');
            }

            // Use the same API key approach as the working CMS version
            const apiKey = getBasisTheoryApiKey(); // Auto-detects environment

            const bt3ds = basisTheory3ds(apiKey);


            const session = await bt3ds.createSession({
                tokenId: paymentInstrument.token,
            });

            // Create session through our API
            const threedsSession = await threedsResource.createSession({
                provider: 'basis_theory',
                sessionData: session,
                paymentInstrumentId: paymentInstrument.id,
            });

            return threedsSession;
        },
        [threedsResource, basisTheory3ds],
    );

    // Generic createSession method that supports multiple providers
    const createSession = useCallback(
        async (paymentInstrument: PaymentInstrument, options?: ThreedsOptions): Promise<ThreedsSession> => {
            const provider = options?.provider || defaultProvider;

            setIsLoading(true);
            setError(null);

            try {
                let session: ThreedsSession;

                if (provider === 'basis_theory') {
                    session = await createBasisTheorySession(paymentInstrument);
                } else {
                    throw new Error(`Unsupported 3DS provider: ${String(provider)}`);
                }

                return session;
            } catch (err) {
                const error = err instanceof Error ? err : new Error('Failed to create 3DS session');
                setError(error);
                throw error;
            } finally {
                setIsLoading(false);
            }
        },
        [defaultProvider, createBasisTheorySession],
    );

    // Start a 3DS challenge with BasisTheory
    const startBasisTheoryChallenge = useCallback(
        async (sessionId: string, acsChallengeUrl: string, acsTransactionId: string, threeDSVersion: string) => {
            try {
                if (!basisTheory3ds) {
                    throw new Error('BasisTheory3ds not loaded yet');
                }

                // Use the same API key approach as the working CMS version
                const apiKey = getBasisTheoryApiKey(); // Auto-detects environment
                if (!apiKey) {
                    throw new Error('BasisTheory API key is not set');
                }

                const modal = createThreedsModal({
                    onClose: () => {
                        // Throw error when user closes the modal
                        throw new Error('Authentication was cancelled by the user');
                    },
                });

                const bt3ds = basisTheory3ds(apiKey);


                const challengeCompletion = await bt3ds.startChallenge({
                    sessionId,
                    acsChallengeUrl,
                    acsTransactionId,
                    threeDSVersion: threeDSVersion as '2.1.0' | '2.2.0',
                    containerId: modal.containerId + '-content',
                    mode: 'iframe',
                    timeout: 60000 * 3,
                });

                closeThreedsModal();
                return challengeCompletion;
            } catch (error) {
                closeThreedsModal();
                throw error;
            }
        },
        [basisTheory3ds, createThreedsModal, closeThreedsModal],
    );

    // Generic startChallenge method that supports multiple providers
    const startChallenge = useCallback(
        async (challengeData: ThreedsChallenge, options?: ThreedsOptions): Promise<any> => {
            const provider = options?.provider || defaultProvider;
            setIsLoading(true);
            setError(null);

            try {
                if (provider === 'basis_theory') {
                    return await startBasisTheoryChallenge(
                        challengeData.sessionId,
                        challengeData.acsChallengeUrl,
                        challengeData.acsTransactionId,
                        challengeData.threeDSVersion,
                    );
                } else {
                    throw new Error(`Unsupported 3DS provider: ${String(provider)}`);
                }
            } catch (err) {
                const error = err instanceof Error ? err : new Error('Failed to start 3DS challenge');
                setError(error);
                throw error;
            } finally {
                setIsLoading(false);
            }
        },
        [defaultProvider, startBasisTheoryChallenge],
    );

    return {
        createSession,
        startChallenge,
        isLoading,
        error,
    };
}
